import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import ProfileMenu from '../components/ProfileMenu';

type Market = { id: string; name: string; symbol: string; minStake: number; defaultOn: boolean };
type Strategy = { id: string; name: string; summary: string };
type Row = {
  strategyId: string;
  strategy: string;
  market: string;
  universe: 'live' | 'all';
  pass: boolean;
  countsAsCell: boolean;
  skippedBySizing: number | null;
  inSample: { trades: number; profitFactor: number | null; avgR: number | null };
  outOfSample: { trades: number; profitFactor: number | null; avgR: number | null; maxDrawdownR: number; win: number | null };
  walkForward: { label: string };
};
type Scan = {
  id: string;
  status: string;
  progress: { pct: number; message: string };
  error?: string | null;
  result?: {
    variantCount: number;
    cells: number;
    passes: number;
    expectedPassesByChance: number | null;
    placeboPasses: number;
    placeboTrials: number;
    rows: Row[];
  };
};

const num = (value: number | null, digits = 2) => (value === null || !Number.isFinite(value) ? '—' : value.toFixed(digits));

async function api(path: string, opts?: RequestInit) {
  const res = await fetch(`/api/backtests${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...(opts?.headers || {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

export default function Scan() {
  const [markets, setMarkets] = useState<Market[]>([]);
  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [pickedMarkets, setPickedMarkets] = useState<string[]>([]);
  const [pickedStrategies, setPickedStrategies] = useState<string[]>(['sweep-fade']);
  const [useGrid, setUseGrid] = useState(false);
  const [scanId, setScanId] = useState<string | null>(null);
  const [scan, setScan] = useState<Scan | null>(null);
  const [err, setErr] = useState('');
  const [sort, setSort] = useState<'pf' | 'trades' | 'dd'>('pf');
  const [universe, setUniverse] = useState<'live' | 'all'>('live');

  useEffect(() => {
    api('/markets').then((data) => {
      const list = (data.markets || []) as Market[];
      setMarkets(list);
      setPickedMarkets(list.filter((market) => market.defaultOn).map((market) => market.name));
      setStrategies(data.strategies || []);
    }).catch((error: Error) => setErr(error.message));
  }, []);

  useEffect(() => {
    if (!scanId) return undefined;
    let stop = false;
    let timer = 0;
    const tick = async () => {
      try {
        const data = await api(`/scans/${scanId}`) as Scan;
        if (stop) return;
        setScan(data);
        if (data.status === 'queued' || data.status === 'running') timer = window.setTimeout(tick, 800);
      } catch (error) {
        if (!stop) setErr(error instanceof Error ? error.message : 'Could not read the scan');
      }
    };
    timer = window.setTimeout(tick, 200);
    return () => { stop = true; window.clearTimeout(timer); };
  }, [scanId]);

  async function onRun() {
    setErr('');
    setScan(null);
    try {
      const created = await api('/scans', {
        method: 'POST',
        body: JSON.stringify({ markets: pickedMarkets, strategies: pickedStrategies, useGrid, placeboRuns: 2 }),
      });
      setScanId(created.id);
    } catch (error) {
      setErr(error instanceof Error ? error.message : 'Could not start');
    }
  }

  const rows = useMemo(() => {
    const list = (scan?.result?.rows || []).filter((row) => row.universe === universe);
    return [...list].sort((a, b) => {
      if (sort === 'trades') return b.outOfSample.trades - a.outOfSample.trades;
      if (sort === 'dd') return a.outOfSample.maxDrawdownR - b.outOfSample.maxDrawdownR;
      return (b.outOfSample.profitFactor ?? -1) - (a.outOfSample.profitFactor ?? -1);
    });
  }, [scan, sort, universe]);

  function toggle(list: string[], value: string, on: boolean): string[] {
    if (on) return list.includes(value) ? list : [...list, value];
    return list.filter((item) => item !== value);
  }

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900 dark:bg-zinc-950 dark:text-zinc-100">
      <header className="sticky top-0 z-10 border-b border-zinc-200 bg-zinc-50/95 px-3 py-3 dark:border-zinc-800 dark:bg-zinc-950/95">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <div>
            <h1 className="text-lg font-bold">Research scan</h1>
            <p className="text-xs text-zinc-500">Many markets × strategies. Research only — no orders, no IG login.</p>
          </div>
          <span className="flex items-center gap-3">
            <Link to="/backtests" className="text-xs underline">Single backtest</Link>
            <Link to="/" className="text-xs underline">Console</Link>
            <ProfileMenu />
          </span>
        </div>
      </header>
      <main className="mx-auto grid max-w-6xl gap-4 px-3 py-4 lg:grid-cols-[320px_1fr]">
        <section className="space-y-3 rounded-lg border border-zinc-200 bg-white p-3 text-xs dark:border-zinc-800 dark:bg-zinc-900">
          <h2 className="font-semibold">Markets</h2>
          <div className="max-h-64 space-y-1 overflow-auto">
            {markets.map((market) => (
              <label key={market.id} className="flex items-center gap-2">
                <Checkbox checked={pickedMarkets.includes(market.name)} onCheckedChange={(value) => setPickedMarkets((curr) => toggle(curr, market.name, value === true))} />
                <span>{market.name}</span>
                <span className="text-zinc-500">{market.symbol}{market.minStake !== 0.5 ? ` · min £${market.minStake}` : ''}</span>
              </label>
            ))}
          </div>
          <h2 className="font-semibold">Strategies</h2>
          {strategies.map((strategy) => (
            <label key={strategy.id} className="flex items-start gap-2">
              <Checkbox checked={pickedStrategies.includes(strategy.id)} onCheckedChange={(value) => setPickedStrategies((curr) => toggle(curr, strategy.id, value === true))} />
              <span><span className="font-medium">{strategy.name}</span><span className="block text-zinc-500">{strategy.summary}</span></span>
            </label>
          ))}
          <label className="flex items-center gap-2">
            <Checkbox checked={useGrid} onCheckedChange={(value) => setUseGrid(value === true)} />
            Include each strategy’s parameter grid
          </label>
          {err && <p className="text-sm text-red-600">{err}</p>}
          <Button type="button" className="w-full" disabled={!pickedMarkets.length || !pickedStrategies.length || scan?.status === 'running'} onClick={() => void onRun()}>
            {scan?.status === 'running' || scan?.status === 'queued' ? 'Scanning…' : 'Run scan'}
          </Button>
        </section>
        <section className="space-y-3">
          {scan && (scan.status === 'running' || scan.status === 'queued') && (
            <div className="rounded-lg border border-zinc-200 bg-white p-3 text-xs dark:border-zinc-800 dark:bg-zinc-900">
              <div className="mb-1 flex justify-between"><span>{scan.progress.message}</span><span>{scan.progress.pct}%</span></div>
              <div className="h-2 overflow-hidden rounded bg-zinc-200 dark:bg-zinc-800"><div className="h-full bg-zinc-900 dark:bg-zinc-100" style={{ width: `${scan.progress.pct}%` }} /></div>
            </div>
          )}
          {scan?.status === 'error' && <p className="text-sm text-red-600">{scan.error}</p>}
          {scan?.result && (
            <div className="rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-900">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-sm">
                <div>
                  <strong>{scan.result.passes}</strong> passed of {scan.result.cells} cells
                  <span className="ml-2 text-xs text-zinc-500">{scan.result.variantCount} variants. Luck check expects {scan.result.expectedPassesByChance === null ? '—' : scan.result.expectedPassesByChance.toFixed(1)} passes ({scan.result.placeboPasses}/{scan.result.placeboTrials} random-direction trials).</span>
                </div>
                <span className="flex gap-2">
                  <Button type="button" size="sm" variant={universe === 'live' ? 'default' : 'outline'} onClick={() => setUniverse('live')}>Live sizing</Button>
                  <Button type="button" size="sm" variant={universe === 'all' ? 'default' : 'outline'} onClick={() => setUniverse('all')}>All signals</Button>
                </span>
              </div>
              <p className="mb-2 text-[11px] text-zinc-500">Pass bar: out-of-sample profit factor &gt; 1.2, at least 30 out-of-sample trades, max drawdown ≤ 15R. Live sizing drops trades below the market minimum stake or above the £15 cap.</p>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-zinc-200 dark:border-zinc-800">
                      <th className="py-1 pr-2">Pass</th>
                      <th className="py-1 pr-2">Strategy</th>
                      <th className="py-1 pr-2">Market</th>
                      <th className="py-1 pr-2"><button type="button" className="underline" onClick={() => setSort('trades')}>OOS n</button></th>
                      <th className="py-1 pr-2"><button type="button" className="underline" onClick={() => setSort('pf')}>OOS PF</button></th>
                      <th className="py-1 pr-2">Avg R</th>
                      <th className="py-1 pr-2"><button type="button" className="underline" onClick={() => setSort('dd')}>Max DD</button></th>
                      <th className="py-1 pr-2">Blocks</th>
                      <th className="py-1">Skipped</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={`${row.universe}-${row.strategyId}-${row.market}-${row.outOfSample.trades}-${row.inSample.trades}`} className="border-b border-zinc-100 dark:border-zinc-800">
                        <td className="py-1 pr-2">{row.pass ? 'Yes' : ''}</td>
                        <td className="py-1 pr-2">{row.strategy}</td>
                        <td className="py-1 pr-2">{row.market}</td>
                        <td className="py-1 pr-2 tabular-nums">{row.outOfSample.trades}</td>
                        <td className="py-1 pr-2 tabular-nums">{num(row.outOfSample.profitFactor)}</td>
                        <td className="py-1 pr-2 tabular-nums">{num(row.outOfSample.avgR)}</td>
                        <td className="py-1 pr-2 tabular-nums">{num(row.outOfSample.maxDrawdownR, 1)}R</td>
                        <td className="py-1 pr-2">{row.walkForward.label}</td>
                        <td className="py-1 tabular-nums">{row.skippedBySizing === null ? '—' : `${Math.round(row.skippedBySizing * 100)}%`}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
